import React, { useContext, useEffect, useMemo, useState } from "react"
import moment from "moment"

import { BookingContext } from "../../../utils/providers/booking"
import { ModalContext } from "../../../utils/providers/modal"
import { useReadOnly } from "../../../utils/providers/read-only"
import { db, auth } from "../../../utils/firebase"

import Button from "../../../components/ui/button/button"
import Badge from "../../../components/ui/badge/badge"
import { PlusIcon } from "../../../utils/svgs"

import ConfirmationModal from "./_components/confirmations/modal"
import {
	CONFIRMATION_STATUS,
	ITEM_KIND,
	addConfirmation,
	deleteConfirmation,
	updateConfirmation,
} from "./_components/confirmations/helpers"

import "./_components/confirmations/confirmations.scss"

/**
 * Status badge driven by the latest confirmation entry on an item.
 * No entries → grey "Not confirmed yet". Latest entry CONFIRMED → green.
 * Latest entry NOT_CONFIRMED → amber (supplier reported nothing on file).
 */
function StatusBadge({ status }) {
	if (status === CONFIRMATION_STATUS.CONFIRMED) {
		return <Badge type="POSITIVE" label="Confirmed" />
	}
	if (status === CONFIRMATION_STATUS.NOT_CONFIRMED) {
		return <Badge type="AMBER" label="Action needed" />
	}
	return <Badge type="" label="Not confirmed yet" />
}

const formatDate = (ts) => {
	if (!ts) return "—"
	if (ts.seconds) return moment(ts.seconds, "X").format("DD/MM/YYYY HH:mm")
	return moment(ts).format("DD/MM/YYYY HH:mm")
}

/**
 * Cache uid → display-name resolutions for the session. Same pattern as the
 * activity feed entry component — avoids hammering Firestore when the same
 * agent has logged many entries.
 */
const userNameCache = {}
const resolveUserName = async (uid) => {
	if (!uid) return null
	if (userNameCache[uid]) return userNameCache[uid]
	const doc = await db.doc(`users/${uid}`).get()
	if (!doc.exists) return null
	const { first_name, last_name } = doc.data()
	const name = `${first_name || ""} ${last_name || ""}`.trim() || null
	userNameCache[uid] = name
	return name
}

function EntryAuthor({ uid }) {
	const [name, setName] = useState(null)
	useEffect(() => {
		let cancelled = false
		resolveUserName(uid).then((n) => {
			if (!cancelled) setName(n)
		})
		return () => {
			cancelled = true
		}
	}, [uid])
	if (!uid) return null
	return <span>{name || "Unknown"}</span>
}

/**
 * One item row in the Confirmations tab. Subscribes to its own confirmations
 * subcollection (ordered newest-first) so the parent doesn't need to thread
 * per-item state through. Exposes its latest-status to the parent via the
 * `onStatus` callback so the X-of-Y counter can stay accurate.
 */
function ItemRow({ bookingID, kind, itemID, itemLabel, subtitle, readOnly, onEdit, onAdd, onDelete }) {
	const [entries, setEntries] = useState([])
	const [loading, setLoading] = useState(true)
	const [expanded, setExpanded] = useState(false)

	useEffect(() => {
		const collectionPath = kind === ITEM_KIND.HOTEL
			? `bookings/${bookingID}/hotels/${itemID}/confirmations`
			: kind === ITEM_KIND.ROUND
				? `bookings/${bookingID}/rounds/${itemID}/confirmations`
				: kind === ITEM_KIND.TRANSFER
					? `bookings/${bookingID}/transfer_confirmations`
					: `bookings/${bookingID}/flight_confirmations`

		const unsub = db
			.collection(collectionPath)
			.orderBy("created", "desc")
			.onSnapshot((snap) => {
				setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
				setLoading(false)
			})
		return () => unsub()
	}, [bookingID, kind, itemID])

	const latestStatus = entries[0]?.status || null

	return (
		<div className="confirmations-row">
			<div className="confirmations-row-header">
				<div className="confirmations-row-meta">
					<p className="confirmations-row-title">{itemLabel || "Unnamed item"}</p>
					{subtitle && <p className="confirmations-row-subtitle">{subtitle}</p>}
				</div>

				<div className="confirmations-row-actions">
					<StatusBadge status={latestStatus} />

					{entries.length > 0 && (
						<button
							type="button"
							className="confirmations-row-toggle"
							onClick={() => setExpanded((v) => !v)}>
							{expanded ? "Hide" : "Show"} {entries.length} {entries.length === 1 ? "entry" : "entries"}
						</button>
					)}

					{!readOnly && (
						<Button
							badge={true}
							small={true}
							icon={<PlusIcon />}
							label="Add"
							onClick={() => onAdd?.()}
						/>
					)}
				</div>
			</div>

			{loading && (
				<div className="confirmations-row-empty">
					<small>Loading…</small>
				</div>
			)}

			{!loading && expanded && entries.length > 0 && (
				<ul className="confirmations-entries">
					{entries.map((entry) => (
						<li key={entry.id} className="confirmations-entry">
							<div className="confirmations-entry-header">
								<div>
									<StatusBadge status={entry.status} />
									{entry.reference && (
										<span className="confirmations-entry-ref">Ref: {entry.reference}</span>
									)}
								</div>
								{!readOnly && (
									<div className="confirmations-entry-actions">
										<button
											type="button"
											className="confirmations-entry-link"
											onClick={() => onEdit?.(entry)}>
											Edit
										</button>
										<button
											type="button"
											className="confirmations-entry-link is-danger"
											onClick={() => onDelete?.(entry)}>
											Delete
										</button>
									</div>
								)}
							</div>

							{entry.notes && <p className="confirmations-entry-notes">{entry.notes}</p>}

							<div className="confirmations-entry-meta">
								{entry.confirmed_by && <span>Supplier contact: {entry.confirmed_by}</span>}
								{entry.received_date && <span>Received {formatDate(entry.received_date)}</span>}
								<EntryAuthor uid={entry.user} />
							</div>
						</li>
					))}
				</ul>
			)}
		</div>
	)
}

/**
 * Hotel row wrapper — pulls the hotel doc to derive the resort name + check-in
 * date as the row subtitle. The resort name lookup uses the booking-context
 * `hotels` map (id → name) to avoid extra reads.
 *
 * Re-binds the onAdd/onEdit/onDelete callbacks so the resolved label flows
 * up to the modal (and from there into the activity-log line).
 */
function HotelRowWrapper({ onAdd, onEdit, onDelete, ...props }) {
	const { hotels } = useContext(BookingContext)
	const [hotelDoc, setHotelDoc] = useState(null)

	useEffect(() => {
		const unsub = db.doc(`bookings/${props.bookingID}/hotels/${props.itemID}`).onSnapshot((snap) => {
			setHotelDoc(snap.data() || null)
		})
		return () => unsub()
	}, [props.bookingID, props.itemID])

	const itemLabel = hotelDoc?.resortID ? hotels?.[hotelDoc.resortID] || "Hotel" : "Hotel (no resort set)"
	const subtitle = hotelDoc?.check_in?.seconds
		? `Check-in ${moment(hotelDoc.check_in.seconds, "X").format("DD/MM/YYYY")}${hotelDoc.nights ? ` · ${hotelDoc.nights} night${hotelDoc.nights === 1 ? "" : "s"}` : ""}`
		: null

	return (
		<ItemRow
			{...props}
			itemLabel={itemLabel}
			subtitle={subtitle}
			onAdd={() => onAdd?.(itemLabel)}
			onEdit={(entry) => onEdit?.(entry, itemLabel)}
			onDelete={(entry) => onDelete?.(entry, itemLabel)}
		/>
	)
}

/**
 * Round row wrapper — derives course name + tee time from the round doc.
 */
function RoundRowWrapper({ onAdd, onEdit, onDelete, ...props }) {
	const { courses } = useContext(BookingContext)
	const [roundDoc, setRoundDoc] = useState(null)

	useEffect(() => {
		const unsub = db.doc(`bookings/${props.bookingID}/rounds/${props.itemID}`).onSnapshot((snap) => {
			setRoundDoc(snap.data() || null)
		})
		return () => unsub()
	}, [props.bookingID, props.itemID])

	const itemLabel = roundDoc?.courseID ? courses?.[roundDoc.courseID] || "Round" : "Round (no course set)"
	const subtitle = roundDoc?.tee_time?.seconds
		? `Tee off ${moment(roundDoc.tee_time.seconds, "X").format("DD/MM/YYYY HH:mm")}`
		: null

	return (
		<ItemRow
			{...props}
			itemLabel={itemLabel}
			subtitle={subtitle}
			onAdd={() => onAdd?.(itemLabel)}
			onEdit={(entry) => onEdit?.(entry, itemLabel)}
			onDelete={(entry) => onDelete?.(entry, itemLabel)}
		/>
	)
}

/**
 * Confirmations tab — lists every supplier-touching item on the booking
 * (hotels, rounds, transfer, flight) with a status badge and per-item
 * confirmation history. Confirmations live in subcollections beneath each
 * item; see helpers.js for the exact paths.
 */
export default function Confirmations() {
	const { bookingID, hotelsOrder, roundsOrder, transferID, transfer, booking } = useContext(BookingContext)
	const { showModal, dismissModal } = useContext(ModalContext)
	const readOnly = useReadOnly()

	const [modalState, setModalState] = useState(null)

	const items = useMemo(() => {
		const list = []
		;(hotelsOrder || []).forEach((hid) => list.push({ key: `hotel:${hid}`, kind: ITEM_KIND.HOTEL, itemID: hid }))
		;(roundsOrder || []).forEach((rid) => list.push({ key: `round:${rid}`, kind: ITEM_KIND.ROUND, itemID: rid }))
		if (transferID) {
			list.push({ key: "transfer", kind: ITEM_KIND.TRANSFER, itemID: null, itemLabel: transfer?.name || "Transfer" })
		}
		if (booking?.flight && booking.flight.itineraryID) {
			list.push({
				key: "flight",
				kind: ITEM_KIND.FLIGHT,
				itemID: null,
				itemLabel: `Flight (${booking.flight.itineraryID})`,
			})
		}
		return list
	}, [hotelsOrder, roundsOrder, transferID, transfer, booking])

	const totalItems = items.length

	const openAdd = (item, resolvedLabel) => setModalState({ mode: "ADD", item: { ...item, itemLabel: resolvedLabel || item.itemLabel } })
	const openEdit = (item, entry, resolvedLabel) => setModalState({ mode: "EDIT", item: { ...item, itemLabel: resolvedLabel || item.itemLabel }, entry })
	const openDelete = (item, entry, resolvedLabel) => {
		const resolvedItem = { ...item, itemLabel: resolvedLabel || item.itemLabel }
		const itemLabel = resolvedItem.itemLabel || resolveItemLabel(resolvedItem)
		const refSuffix = entry.reference ? ` (ref ${entry.reference})` : ""
		showModal({
			type: "ALERT",
			title: "Delete this confirmation?",
			body: `This will remove the confirmation entry for ${itemLabel}${refSuffix}. An audit line is still added to the booking's activity feed.`,
			cancel: {
				label: "Cancel",
				action: () => null,
			},
			next: {
				label: "Yes, delete",
				action: async () => {
					const userID = auth.currentUser?.uid || null
					await deleteConfirmation({
						bookingID,
						kind: resolvedItem.kind,
						itemID: resolvedItem.itemID,
						itemLabel,
						confirmationID: entry.id,
						snapshot: entry,
						userID,
					})
					dismissModal()
				},
			},
		})
	}
	const closeModal = () => setModalState(null)

	const handleModalSave = async (entry) => {
		const userID = auth.currentUser?.uid || null
		if (modalState.mode === "ADD") {
			await addConfirmation({
				bookingID,
				kind: modalState.item.kind,
				itemID: modalState.item.itemID,
				itemLabel: modalState.item.itemLabel || resolveItemLabel(modalState.item),
				entry,
				userID,
			})
		} else if (modalState.mode === "EDIT") {
			await updateConfirmation({
				bookingID,
				kind: modalState.item.kind,
				itemID: modalState.item.itemID,
				itemLabel: modalState.item.itemLabel || resolveItemLabel(modalState.item),
				confirmationID: modalState.entry.id,
				entry,
				userID,
			})
		}
		closeModal()
	}

	if (totalItems === 0) {
		return (
			<div className="confirmations-tab">
				<p className="confirmations-empty">
					No supplier items on this booking yet. Add hotels, rounds, transfers or a flight first and they'll show up here for confirmation tracking.
				</p>
			</div>
		)
	}

	const renderRow = (item) => {
		const common = {
			bookingID,
			kind: item.kind,
			itemID: item.itemID,
			readOnly,
			onAdd: (resolvedLabel) => openAdd(item, resolvedLabel),
			onEdit: (entry, resolvedLabel) => openEdit(item, entry, resolvedLabel),
			onDelete: (entry, resolvedLabel) => openDelete(item, entry, resolvedLabel),
		}

		if (item.kind === ITEM_KIND.HOTEL) {
			return <HotelRowWrapper key={item.key} {...common} />
		}
		if (item.kind === ITEM_KIND.ROUND) {
			return <RoundRowWrapper key={item.key} {...common} />
		}
		return <ItemRow key={item.key} {...common} itemLabel={item.itemLabel} />
	}

	const hotelItems = items.filter((it) => it.kind === ITEM_KIND.HOTEL)
	const roundItems = items.filter((it) => it.kind === ITEM_KIND.ROUND)
	const transferItems = items.filter((it) => it.kind === ITEM_KIND.TRANSFER)
	const flightItems = items.filter((it) => it.kind === ITEM_KIND.FLIGHT)

	return (
		<div className="confirmations-tab">
			{hotelItems.length > 0 && (
				<section className="confirmations-section">
					<h4 className="confirmations-section-title">Hotels</h4>
					{hotelItems.map(renderRow)}
				</section>
			)}

			{roundItems.length > 0 && (
				<section className="confirmations-section">
					<h4 className="confirmations-section-title">Rounds</h4>
					{roundItems.map(renderRow)}
				</section>
			)}

			{transferItems.length > 0 && (
				<section className="confirmations-section">
					<h4 className="confirmations-section-title">Transfers</h4>
					{transferItems.map(renderRow)}
				</section>
			)}

			{flightItems.length > 0 && (
				<section className="confirmations-section">
					<h4 className="confirmations-section-title">Flight</h4>
					{flightItems.map(renderRow)}
				</section>
			)}

			{modalState && (
				<ConfirmationModal
					mode={modalState.mode}
					item={modalState.item}
					entry={modalState.entry}
					onSave={handleModalSave}
					onClose={closeModal}
				/>
			)}
		</div>
	)
}

/**
 * Fallback label resolver — used when the parent didn't pre-compute an item
 * label (transfer / flight always do; hotels/rounds rely on this when their
 * subscribed wrapper hasn't loaded yet).
 */
function resolveItemLabel(item) {
	if (item.itemLabel) return item.itemLabel
	if (item.kind === ITEM_KIND.HOTEL) return "Hotel"
	if (item.kind === ITEM_KIND.ROUND) return "Round"
	if (item.kind === ITEM_KIND.TRANSFER) return "Transfer"
	if (item.kind === ITEM_KIND.FLIGHT) return "Flight"
	return "Item"
}
